/**
 * Notification System for Cedar Heights Music Academy Admin System
 * 
 * This component provides a comprehensive notification system with:
 * - Toast notifications (snackbars) for temporary messages
 * - Alert banners for persistent notifications
 * - Multiple severity levels (success, error, warning, info)
 * - Auto-dismiss functionality
 * - Action buttons and custom content
 * - Queue management for multiple notifications
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  Slide,
  SlideProps,
  Box,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Notification types
export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface NotificationAction {
  label: string;
  onClick: () => void;
  color?: 'inherit' | 'primary' | 'secondary';
}

export interface NotificationOptions {
  id?: string;
  severity: NotificationSeverity;
  title?: string;
  message: string;
  autoHideDuration?: number | null;
  persistent?: boolean;
  actions?: NotificationAction[];
  onClose?: () => void;
}

interface NotificationContextType {
  showNotification: (options: NotificationOptions) => string;
  hideNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

// Create notification context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Slide transition component
const SlideTransition = (props: SlideProps) => {
  return <Slide {...props} direction="up" />;
};

/**
 * Notification Provider component that manages the notification state
 */
interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 3,
}) => {
  const [notifications, setNotifications] = useState<(NotificationOptions & { id: string })[]>([]);

  const showNotification = useCallback((options: NotificationOptions): string => {
    const id = options.id || `notification-${Date.now()}-${Math.random()}`;
    const notification = { ...options, id };

    setNotifications(prev => {
      const newNotifications = [...prev, notification];
      // Limit the number of notifications
      if (newNotifications.length > maxNotifications) {
        return newNotifications.slice(-maxNotifications);
      }
      return newNotifications;
    });

    return id;
  }, [maxNotifications]);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleClose = (id: string, onClose?: () => void) => {
    hideNotification(id);
    onClose?.();
  };

  return (
    <NotificationContext.Provider value={{ showNotification, hideNotification, clearAllNotifications }}>
      {children}
      
      {/* Render notifications */}
      {notifications.map((notification, index) => (
        <NotificationSnackbar
          key={notification.id}
          notification={notification}
          onClose={() => handleClose(notification.id, notification.onClose)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          style={{
            bottom: 16 + index * 70, // Stack notifications
          }}
        />
      ))}
    </NotificationContext.Provider>
  );
};

/**
 * Individual notification snackbar component
 */
interface NotificationSnackbarProps {
  notification: NotificationOptions & { id: string };
  onClose: () => void;
  anchorOrigin: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  style?: React.CSSProperties;
}

const NotificationSnackbar: React.FC<NotificationSnackbarProps> = ({
  notification,
  onClose,
  anchorOrigin,
  style,
}) => {
  const theme = useTheme();

  const getIcon = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return null;
    }
  };

  return (
    <Snackbar
      open={true}
      autoHideDuration={notification.persistent ? null : (notification.autoHideDuration ?? 6000)}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      TransitionComponent={SlideTransition}
      style={style}
      sx={{
        '& .MuiSnackbarContent-root': {
          padding: 0,
        },
      }}
    >
      <Alert
        severity={notification.severity}
        icon={getIcon(notification.severity)}
        onClose={onClose}
        sx={{
          minWidth: 300,
          maxWidth: 500,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Custom action buttons */}
            {notification.actions?.map((action, index) => (
              <IconButton
                key={index}
                size="small"
                color={action.color || 'inherit'}
                onClick={action.onClick}
                sx={{ fontSize: '0.875rem' }}
              >
                {action.label}
              </IconButton>
            ))}
            
            {/* Close button */}
            <IconButton
              size="small"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        {notification.title && (
          <AlertTitle sx={{ marginBottom: notification.message ? 1 : 0 }}>
            {notification.title}
          </AlertTitle>
        )}
        {notification.message}
      </Alert>
    </Snackbar>
  );
};

/**
 * Hook to use the notification system
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Convenience hooks for different notification types
 */
export const useNotificationHelpers = () => {
  const { showNotification } = useNotifications();

  const showSuccess = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({
      severity: 'success',
      message,
      ...options,
    });
  }, [showNotification]);

  const showError = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({
      severity: 'error',
      message,
      persistent: true, // Errors should be persistent by default
      ...options,
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({
      severity: 'warning',
      message,
      ...options,
    });
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: Partial<NotificationOptions>) => {
    return showNotification({
      severity: 'info',
      message,
      ...options,
    });
  }, [showNotification]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

/**
 * Alert Banner component for persistent notifications
 */
interface AlertBannerProps {
  severity: NotificationSeverity;
  title?: string;
  message: string;
  actions?: NotificationAction[];
  onClose?: () => void;
  closable?: boolean;
}

export const AlertBanner: React.FC<AlertBannerProps> = ({
  severity,
  title,
  message,
  actions,
  onClose,
  closable = true,
}) => {
  const theme = useTheme();

  return (
    <Alert
      severity={severity}
      onClose={closable ? onClose : undefined}
      sx={{
        marginBottom: theme.spacing(2),
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      action={
        actions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions.map((action, index) => (
              <IconButton
                key={index}
                size="small"
                color={action.color || 'inherit'}
                onClick={action.onClick}
                sx={{ fontSize: '0.875rem' }}
              >
                {action.label}
              </IconButton>
            ))}
          </Box>
        )
      }
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  );
};

export default NotificationProvider;